import modalView from './modalView.html';
import angular from 'angular';
import _ from 'lodash';
import { ensure } from 'components/sharpr/ensure/ensure';
import { zIndexManager } from 'components/sharpr/zIndex/manager/zIndexManager';
import { device } from '../device/device.js';
import './modalStyles.css';

let numOpen = 0;

export default function modalComponent() {
	return {
		restrict: 'E',
		scope: {},
		transclude: true,
		bindToController: {
			heading: '@',
			open: '=',
			onClose: '&',
			width: '@',
			height: '@',
			attemptFocus: '<',
			onBeforeClose: '<?',
			preventClose: '<?',
			displayKnowledgebaseLink: '<?',
			knowledgebaseLinkUrl: '<?',
		},
		controllerAs: 'vm',
		controller: modalComponentCtrl,
		template: modalView,
	};
}

class modalComponentCtrl {
	constructor($scope, $element) {
		'ngInject';
		// we need a reference to modal and main
		this.cover = $element;
		this.element = this.cover[0];
		this.modal = angular.element(this.cover[0].querySelector('.sh-modal-container'));
		this.main = angular.element(document.querySelector('main'));
		// handle clicks on the cover (not inside the modal)
		this.cover.on('click', this.handleCoverClick.bind(this));
		// detach modal element
		this.element.parentNode.removeChild(this.element);
		// add classes that allow css transitions
		this.setClassState('closed');
		// setup watches
		$scope.$watchGroup(['vm.width', 'vm.height'], this.resize.bind(this));
		$scope.$watch('vm.open', this.toggle.bind(this));
		// start closed
		this.isOpen = false;
		if (device.isPhone()) {
			this.maxWidth = '100vw';
			this.maxHeight = '100vh';
		} else {
			this.maxWidth = 'calc(100vw - 40px)';
			this.maxHeight = 'calc(100vh - 40px)';
		}
	}

	handleCoverClick(evt) {
		if (evt.target !== this.cover[0]) {
			// click inside modal and not on cover
			return;
		}
		if (!this.preventClose) {
			this.toggle(false);
		}
	}

	toggle(shouldBeOpen) {
		if (this.isOpen == !!shouldBeOpen || this.isConfirming) {
			// we are already in the proper state
			return;
		}
		// initiate css transitions
		if (shouldBeOpen) {
			this.launch();
		} else if (_.isFunction(this.onBeforeClose)) {
			this.isConfirming = true;
			ensure.promise(this.onBeforeClose()).then(
				() => {
					this.isConfirming = false;
					this.close();
				},
				() => {
					this.isConfirming = false;
				}
			);
		} else {
			this.close();
		}
	}

	launch() {
		this.isOpen = true;
		numOpen++;
		this._preventDocumentScroll();
		// append modal to body and unhide if hidden
		document.body.appendChild(this.element);
		this.element.style.display = 'flex';
		// ensure it sits on top
		zIndexManager.bringToTop(this.cover);
		// timeout to allow node to append and css class to take hold
		// so that css transition will kick in
		setTimeout(() => this.setClassState('open'), 0);
		if (this.attemptFocus) {
			setTimeout(() => {
				try {
					const modals = document.querySelectorAll('.sh-modal-body');
					const input = modals[modals.length - 1].querySelector('.form-input');
					input.select();
					input.focus();
				} catch (e) {}
			}, 100);
		}
	}

	close() {
		this.isOpen = false;
		this.open = false;
		numOpen--;
		if (this.cover) {
			this.cover.css('display', 'flex');
			this.cover.one('transitionend', () => {
				// hide modal
				if (this.element) {
					this.element.style.display = 'none';
				}
			});
		}
		this.setClassState('closed');
		this._allowDocumentScroll();
		// fire bound callback
		this.onClose();
	}

	$onDestroy() {
		if (this.isOpen) {
			this.close();
		}
		this.cover.remove();
		this.cover = null;
		this.element = null;
	}

	resize(size) {
		let isNumeric = n => String(n).match(/^\d+$/);
		let [w, h] = size;
		let width, height;
		let reset = {
			top: 'auto',
			right: 'auto',
			bottom: 'auto',
			left: 'auto',
			width: 'auto',
			height: 'auto',
		};
		this.modal.css(reset);
		// set width
		if (!w || w == 'full') {
			width = this.maxWidth;
		} else if (isNumeric(w)) {
			width = w + 'px';
		} else {
			width = w;
		}
		// set height
		if (!h || h == 'full') {
			height = this.maxHeight;
		} else if (isNumeric(h)) {
			height = h + 'px';
		} else {
			height = h;
		}
		this.modal.css({ width, height });
	}

	setClassState(state) {
		let nodes = [this.cover, this.modal];
		if (state == 'open' || numOpen === 0) {
			// if we have two modals open and close one,
			// don't remove the sh-modal-open class from <main>
			nodes.push(this.main);
		}
		let toAdd = state == 'open' ? 'sh-modal-open' : 'sh-modal-closed';
		let toRemove = state == 'open' ? 'sh-modal-closed' : 'sh-modal-open';
		nodes.forEach(node => {
			node && node.removeClass(toRemove).addClass(toAdd);
		});
	}

	_preventDocumentScroll() {
		const docEl = document.documentElement;
		this._initialDocumentOverflow = docEl.style.overflowY;
		docEl.style.overflowY = 'hidden';
	}

	_allowDocumentScroll() {
		const docEl = document.documentElement;
		docEl.style.overflowY = this._initialDocumentOverflow;
	}
}
